import { Fragment, ReactElement, Suspense, useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { Router, matchPath, useHistory } from 'react-router';
import * as Sentry from '@sentry/react';

import {
    APP_PREFIX,
    GO_TO_APPOINTMENTS,
    RouteParams,
    GO_TO_UHI_LOGIN,
    OUT_PAGES,
    GO_TO_ADD_NEW_PATIENT,
} from './routes/paths';

import routes, { AppRoute } from './routes/routes';

import configureStore from './store';
import hotkeys from 'hotkeys-js';

import {
    getAllPrescription,
    getDoc,
    getPrescriptionById,
    getSelectedClinicId,
} from './store/idbHandler';
import AuthWrapper from './pages/AuthWrapper';
import LoadApp from './components/LoadApp';
import {
    getCareCanvasPreview,
    getIFrameDialogUrl,
    getIsGlobalSearchVisible,
    getIsGoogleReviewDialogVisible,
    getIsPrivacyDialogVisible,
    getShowEkaCommDialogVisible,
} from './store/selectors/session';
import PrivacyDialog from './components/common/PrivacyDialog';
import { AnimatePresence } from 'framer-motion';
import IFrameDialog from './components/common/IFrameDialog';
import { APPZI_URL, BOOKING_SLUG, SENTRY_DNS } from './api/constants';
import { CURIO_IFRAME_ID } from './pages/MyCalendar';
import { initTracking, track, TRACK_ROUTE_MISSING } from './tracking';
import UhiLogin from './pages/UhiLogin';
import RescrictionWrapper from './pages/RescrictionWrapper';
import { his } from './pages/history';
import exposeBridge from './Bridge';
import { ABDMBanner } from './components/mobilenumber/ABDMBanner';
import {
    getIsABDMBannerVisible,
    getIsHealthWeekVisible,
    getIsSupportTicketsVisible,
    getIsTroubleshootMySystemVisible,
} from './store/selectors/prescriptionPad';
import GoogleReviewDialog2 from './components/common/GoogleReviewModal2';
import { HealthWeekModal } from './components/common/HealthWeek';
import GlobalSearchModal from './components/globalsearch/GlobalSearchModal';
import { setCareCanvasPreview, setKhataCredits, toggleGlobalSearch } from './store/actions/session';
import { isOnboardedForLab, isOnboardedForPharma } from './pages/utils';
import { KHATA_CREDITS } from './pages/MyKhata';
import StorageDetailsDialog from './components/common/StorageDetailsDialog';
import EkaCommunicationsDialog from './components/common/EkaCommunicationsDialog';
import AllSupportTickets from './components/common/SupportTickets/AllSupportTickets';
import TroubleshootMySystemDrawer from './components/common/TroubleshootMySystem/TroubleshootMySystemDrawer';
import PreviewTemplateModal from './components/prescriptionPad/rightPanel/workspace/CareCanvas/PreviewTemplate';
import { CareCanvasTemplate } from './store/types/CareCanvas';
import { FULL_GENDER_CHARS, Gender, NewPatientLocationState } from './pages/NewPatient';
import CreateABHAModalSDK from './components/abha/CreateABHAModalSdk';
import { TProfileRecord } from './bridge/types';
import { notification } from 'antd';
import LogoutModal from './components/common/LogoutModal';

let isPageBlocked = false;
let appziScript: HTMLScriptElement | undefined;

export const getIsPageBlocked = (): boolean => {
    return isPageBlocked;
};

export const setIsPageBlocked = (value: boolean): void => {
    console.log('value', value);
    isPageBlocked = value;
};

initTracking();

export const setupUserInSentry = (): void => {
    if (!SENTRY_DNS) {
        return;
    }

    try {
        const doc = getDoc();

        if (!doc) {
            Sentry.setUser(null);
            return;
        }

        Sentry.setUser({
            id: doc._id,
            username: [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
                .filter(Boolean)
                .join(' '),
        });
    } catch (e) {
        console.error(e);
    }
};

// Virtuoso's resize observer can this error,
// which is caught by DnD and aborts dragging.
window.addEventListener('error', (e) => {
    if (
        e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message === 'ResizeObserver loop limit exceeded' ||
        e.message?.includes('ResizeObserver loop completed') ||
        e.message?.includes('db not found') ||
        e.message?.includes('Key already exists in the object store') ||
        e.message?.includes("reading 'docs'") ||
        e.message?.includes('IDBObjectStore') ||
        e.message?.includes('t.docs')
    ) {
        e.stopImmediatePropagation();
    }
});

SENTRY_DNS &&
    Sentry.init({
        dsn: SENTRY_DNS,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        release: APP_VERSION,
        tracesSampleRate: 0,
        beforeSend(event) {
            if (
                event.message === 'ResizeObserver loop limit exceeded' ||
                event.message?.startsWith(
                    'ResizeObserver loop completed with undelivered notifications',
                ) ||
                event.message?.includes('ResizeObserver loop') ||
                event.message?.includes('db not found') ||
                event.message?.includes('Key already exists in the object store') ||
                event.message?.includes("reading 'docs'") ||
                event.message?.includes('IDBObjectStore') ||
                event.message?.includes('t.docs')
            ) {
                return null;
            }
            return event;
        },
        ignoreErrors: [
            // Ignore ResizeObserver errors
            'ResizeObserver loop completed with undelivered notifications.',
            /ResizeObserver/i, // Ignore any error containing "ResizeObserver"
            /db not found/i, // Ignore any error containing "db not found"
            /Key already exists in the object store/i, // Ignore any error containing "Key already exists in the object store."
            /reading 'docs'/i, // Ignore any error containing "reading 'docs'"
            /IDBObjectStore/i, // Ignore any error containing "IDBObjectStore"
            /t.docs/i, // Ignore any error containing "t.docs"
        ],
    });
SENTRY_DNS && setupUserInSentry();

try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    $crisp.push([
        'on',
        'chat:initiated',
        () => {
            setUpChatSession();
            tackeChtBoxVisibility();
        },
    ]);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    $crisp.push([
        'on',
        'session:loaded',
        () => {
            setUpChatSession();
            tackeChtBoxVisibility();
        },
    ]);
} catch (e) {
    console.error(e);
}

try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.clarity('identify', getDoc()?._id);
} catch (error) {
    console.log(error);
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// const initalState = window.__initial_state__;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.getPrescriptionById = getPrescriptionById;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.getAllPrescription = getAllPrescription;

export const { store } = configureStore();

export const getStore = () => {
    return store;
};

interface CurrentRouteWithParams extends AppRoute {
    params: RouteParams;
}

export let cachedRouteParams = {};

export const getCurrentRouteAndParams = (
    url: string,
    search: string,
): CurrentRouteWithParams | void => {
    const searchParams = new URLSearchParams(search);
    let sp: { [k: string]: string | null } = {};

    for (const key of searchParams.keys()) {
        sp = { ...sp, [key]: searchParams.get(key) };
    }

    for (let i = 0; i < routes.length; i++) {
        const mp = matchPath<RouteParams>(url, routes[i]);
        if (mp) {
            cachedRouteParams = {
                ...mp.params,
                ...sp,
                pageTrackingName: routes[i].pageTrackingName,
            };
            return { ...routes[i], params: mp.params };
        } else {
            cachedRouteParams = sp;
        }
    }
};

his.listen((): void => {
    trackRouteChange();
});

export const GetApp: React.FC = (): ReactElement => {
    const dispatch = useDispatch();
    useEffect(() => {
        trackRouteChange();
        const khataCredits = localStorage.getItem(KHATA_CREDITS);
        khataCredits && dispatch(setKhataCredits(Number(khataCredits)));
    }, []);

    return (
        <Fragment>
            <Router history={his}>
                <Suspense fallback={<p>Fallback loading</p>}>
                    <Switch>
                        <Route exact path="/">
                            <Redirect
                                to={GO_TO_APPOINTMENTS(
                                    getDoc()?.profile.professional.default_clinic || '',
                                )}
                            />
                        </Route>
                        <Route exact path={APP_PREFIX}>
                            <Redirect
                                to={GO_TO_APPOINTMENTS(
                                    getDoc()?.profile.professional.default_clinic || '',
                                )}
                            />
                        </Route>
                        <Route path={GO_TO_UHI_LOGIN} component={UhiLogin} />
                        {routes.map(({ path, exact, component: C }) => {
                            const Com = AuthWrapper(C, path);
                            return (
                                <Route
                                    key={path}
                                    path={path}
                                    exact={exact}
                                    render={(props) => {
                                        return (
                                            <RescrictionWrapper {...props}>
                                                <Com {...props} />
                                            </RescrictionWrapper>
                                        );
                                    }}
                                />
                            );
                        })}
                    </Switch>
                </Suspense>
                <Dialogs />
            </Router>
        </Fragment>
    );
};

export const setUpChatSession = (): void => {
    try {
        const doc = getDoc();

        if (!doc) {
            console.error('doc not found while setting up chat session');
            return;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp?.push(['set', 'user:email', [doc.profile.personal.email || `${doc._id}@eka.do`]]);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp.push([
            'set',
            'user:nickname',
            [
                [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
                    .filter(Boolean)
                    .join(' '),
            ],
        ]);

        const sessionData = [
            ['doc_id', doc._id],
            [
                'doc_name',
                [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
                    .filter(Boolean)
                    .join(' '),
            ],
            [
                'saas',
                doc?.profile.professional.saas && doc?.profile.professional.saas_detail?.status
                    ? doc?.profile.professional.saas
                    : '',
            ],
            ['client', 'web'],
            ['onboarded', doc?.profile.professional.onboarded_for?.join(', ') || ''],
            ['cat', doc?.profile.professional.category?.join(', ') || ''],
            ['device', 'web'],
            ['actor', doc.loginmeta?.role || ''],
            ['actor_oid', doc.loginmeta?.userid || ''],
            ['actor_name', doc.loginmeta?.username || ''],
            ['cs_name', doc.profile.professional.cs?.success_manager || ''],
        ];

        doc?.profile.professional.onboarded_for?.forEach((onboardedFor) => {
            sessionData.push([`${onboardedFor}`, '1']);
        });
        doc?.profile.professional.category?.forEach((category) => {
            sessionData.push([`${category}`, '1']);
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp.push(['set', 'session:data', [sessionData]]);

        const segments = ['web', doc.loginmeta?.role];

        segments.push(
            doc.profile.professional.saas && doc.profile.professional.saas_detail?.status
                ? `saas-${doc.profile.professional.saas}`
                : 'UNPAID',
        );

        if (doc.profile.professional.onboarded_for?.includes('HXNG')) {
            segments.push('HXNG');
        } else if (doc.profile.professional.onboarded_for?.includes('HELB')) {
            segments.push('HELB');
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $crisp.push(['set', 'session:segments', [segments, true]]);
    } catch (e) {
        console.error(e);
    }
};

function trackRouteChange() {
    tackeChtBoxVisibility();

    const route = getCurrentRouteAndParams(window.location.pathname, window.location.search);
    if (route && route.pageTrackingName) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        track(route.pageTrackingName, route.params);
    } else {
        track(TRACK_ROUTE_MISSING, { path: window.location.pathname });
    }
}

export const setupAppzi = async (): Promise<void> => {
    const doc = getDoc();
    if (!doc) {
        return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.appziSettings = {
        data: {
            id: doc?._id,
            name: [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
                .filter(Boolean)
                .join(' '),
            role: doc?.loginmeta?.role,
            hem: isOnboardedForPharma().toString(),
            hel: isOnboardedForLab().toString(),
            saas: doc?.profile?.professional?.saas?.toString(),
            hxngnps: hxngNPSDocid.has(doc?._id) ? 'yes' : 'no',
        },
    };
    if (appziScript) {
        return;
    }
    appziScript = document.createElement('script');
    appziScript.src = APPZI_URL;
    document.head.appendChild(appziScript);
};

function tackeChtBoxVisibility() {
    const list = document.getElementsByClassName('crisp-client');
    if (list.length === 1) {
        const url = window.location.pathname;
        if (
            url.indexOf('/prescription/') > -1 ||
            url.indexOf('/prescription-v2/') > -1 ||
            url.indexOf('/patient/') > -1 ||
            url.indexOf('/patients/new/') > -1 ||
            url.indexOf('/visit/call/') > -1 ||
            url.indexOf('/doc-assist-ai/') > -1
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            $crisp.push(['do', 'chat:hide']);
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            $crisp.push(['do', 'chat:show']);
        }
    }
}

const keysPressed: any = {};

const globalSearchBlacklist = [
    '/app/prescription-v2',
    '/app/settings',
    '/app/change-doc',
    '/app/login',
    '/app/patients/new',
];

function canOpenGlobalSearch(pathname: string) {
    return !globalSearchBlacklist.find((p) => pathname.includes(p));
}

function openHandlerGS(event: globalThis.KeyboardEvent) {
    event.preventDefault();

    if (canOpenGlobalSearch(his.location.pathname)) {
        track('GLOBAL_SEARCH', { where: 'cmd+k' });
        store.dispatch(toggleGlobalSearch(true));
    }
}

function closeHandlerGs(event: globalThis.KeyboardEvent) {
    event.preventDefault();
    store.dispatch(toggleGlobalSearch(false));
}

function App(): ReactElement {
    useEffect(() => {
        setupAppzi();
        hotkeys('command+k,ctrl+k,esc', function (event, handler) {
            switch (handler.key) {
                case 'esc':
                    closeHandlerGs(event);
                    break;
                case 'command+k':
                case 'ctrl+k':
                    openHandlerGS(event);
                    break;
            }
        });
    }, []);

    return (
        <Provider store={store}>
            <LoadApp>
                <GetApp />
            </LoadApp>
        </Provider>
    );
}

const Dialogs: React.FC = () => {
    return (
        <AnimatePresence>
            <Privacy />
            <ABDM />
            <IFrame />
            <GoogleReview />
            <GlobalSearch />
            <EkaCommunications />
            <StorageModal />
            {HealthWeek()}
            {SupportTickets()}
            {TroubleshootMySystem()}
            <CareCanvasPreview />
            <LogoutModal />
        </AnimatePresence>
    );
};

const SupportTickets = (): JSX.Element => {
    const isSupportTicketsVisible = useSelector(getIsSupportTicketsVisible);

    return <AnimatePresence>{isSupportTicketsVisible && <AllSupportTickets />}</AnimatePresence>;
};

const TroubleshootMySystem = (): JSX.Element => {
    const isTroubleshootMySystemVisible = useSelector(getIsTroubleshootMySystemVisible);

    return (
        <AnimatePresence>
            {isTroubleshootMySystemVisible && <TroubleshootMySystemDrawer />}
        </AnimatePresence>
    );
};

const Privacy: React.FC = () => {
    const isPrivacyDialogVisible = useSelector(getIsPrivacyDialogVisible);

    return isPrivacyDialogVisible ? <PrivacyDialog /> : null;
};

const ABDM: React.FC = () => {
    const isABDMBannerVisible = useSelector(getIsABDMBannerVisible);

    return isABDMBannerVisible ? <ABDMBanner /> : null;
};

const StorageModal: React.FC = () => {
    return <StorageDetailsDialog />;
};

const GlobalSearch: React.FC = () => {
    const isGlobalSearchVisible = useSelector(getIsGlobalSearchVisible);
    const [newPatientViaAbha, setNewPatientViaAbha] = useState(false);
    const [addPatientDialogDefaultState, setAddPatientDialogDefaultState] = useState<
        NewPatientLocationState | undefined
    >(undefined);

    const location = useLocation();
    const history = useHistory();
    const [flow, setFlow] = useState<'create_abha_with_mobile' | 'login_with_abha'>(
        'create_abha_with_mobile',
    );

    if (location.pathname.startsWith(OUT_PAGES)) {
        return null;
    }

    const toggleAddPtDialogViaAbha = (
        value: boolean,
        state?: NewPatientLocationState,
        flow?: 'create_abha_with_mobile' | 'login_with_abha',
    ): void => {
        setNewPatientViaAbha(value);
        setAddPatientDialogDefaultState(state);
        setFlow(flow || 'create_abha_with_mobile');
    };

    const onAbhaAddPatientSuccess = (profile?: TProfileRecord) => {
        console.log('ABHA Patient Profile:', profile);
        if (!profile) {
            track('ERROR', {
                on: 'abha',
                what: 'add-patient',
                status: 'failed',
                message: 'Profile not found',
            });
            notification.error({
                message: 'Profile not found',
                duration: 5,
            });
            return;
        }

        history.push({
            pathname: GO_TO_ADD_NEW_PATIENT(getSelectedClinicId() || ''),
            state: {
                name: profile.fln,
                phoneNumber: profile.mobile?.replace('+91', ''),
                gender: FULL_GENDER_CHARS[profile.gen || ''] as Gender,
                dob: profile.dob,
                defaultShowAbhaCard: true,
                defaultAbhaId: profile['health-ids']?.[0],
                defaultAbhaPtOid: profile.oid,
            },
        });
    };
    return (
        <Fragment>
            {newPatientViaAbha && (
                <CreateABHAModalSDK
                    onClose={() => toggleAddPtDialogViaAbha(false)}
                    mobile={addPatientDialogDefaultState?.phoneNumber}
                    onComplete={onAbhaAddPatientSuccess}
                    method={flow}
                />
            )}
            <GlobalSearchModal
                key={getDoc()?._id}
                isGlobalSearchVisible={isGlobalSearchVisible}
                close={() => store.dispatch(toggleGlobalSearch(false))}
                onAddPatientViaAbha={(
                    state: NewPatientLocationState,
                    flow: 'create_abha_with_mobile' | 'login_with_abha',
                ) => toggleAddPtDialogViaAbha(true, state, flow)}
            />
        </Fragment>
    );
};

const HealthWeek = (): JSX.Element => {
    const isHealthWeekVisible = useSelector(getIsHealthWeekVisible);
    return <AnimatePresence>{isHealthWeekVisible && <HealthWeekModal />}</AnimatePresence>;
};

const GoogleReview: React.FC = () => {
    const isGoogleReviewDialogVisible = useSelector(getIsGoogleReviewDialogVisible);

    return isGoogleReviewDialogVisible ? (
        <GoogleReviewDialog2 docid={isGoogleReviewDialogVisible} />
    ) : null;
};

const EkaCommunications: React.FC = () => {
    const isShowEkaCommVisible = useSelector(getShowEkaCommDialogVisible);

    return isShowEkaCommVisible ? <EkaCommunicationsDialog /> : null;
};

const IFrame: React.FC = () => {
    const url = useSelector(getIFrameDialogUrl);

    return url ? (
        // TODO id logic can be refactored
        <IFrameDialog id={url.includes(BOOKING_SLUG) ? CURIO_IFRAME_ID : undefined} url={url} />
    ) : null;
};

const CareCanvasPreview: React.FC = () => {
    const careCanvasPreview = useSelector(getCareCanvasPreview);
    const dispatch = useDispatch();
    const closeMe = () => dispatch(setCareCanvasPreview());

    return (
        <PreviewTemplateModal
            template={(careCanvasPreview as unknown) as CareCanvasTemplate}
            closeMe={closeMe}
        />
    );
};

exposeBridge();

export default App;

const hxngNPSDocid = new Set([
    '161459684229004',
    '167398499781078',
    '169770639832143',
    '166030999517438',
    '169692749344518',
    '161479131229772',
    '166961865255587',
    '164585871372316',
    '165155286416627',
    '165986142095915',
    '168957129038752',
    '166313632352152',
    '169159211019317',
    '165304988440871',
    '166737130213400',
    '165924748042740',
    '167178308363601',
    '170687351525654',
    '167748262354268',
    '165217406070228',
    '167283070492825',
    '170057814256155',
    '170368716186411',
    '164069625362006',
    '167534406429238',
    '162287541242226',
    '165070922566035',
    '167472280653753',
    '167697562584672',
    '165416048048457',
    '163213529049534',
    '168692177669604',
    '165943766987173',
    '167431037931505',
    '170427540882040',
    '166547154681363',
    '166990955886653',
    '163031670157376',
    '163827973364881',
    '170755020598068',
    '170799686849676',
    '168233792973351',
    '165666686307482',
    '165373231702757',
    '170773230671587',
    '164793291358072',
    '165536220984737',
    '166261987299680',
    '168319894395411',
    '167782010392813',
    '165994613077088',
    '169376217601278',
    '170789057483537',
    '170379041467145',
    '167281918856786',
    '166919032529838',
    '164205062632124',
    '170963116027581',
    '170192475105885',
    '166065942867862',
    '168422448128027',
    '170195533382034',
    '166201536178037',
    '170325400031562',
    '166469479006865',
    '168438412077587',
    '168494159776242',
    '170204152112075',
    '167290391863905',
    '168260049075371',
    '168768846775852',
    '168438670043414',
    '170996008227113',
    '167810607328785',
    '169029549601740',
    '169911189777617',
    '169098740033736',
    '171049311855662',
    '167980786179778',
    '170246855966645',
    '168053620084773',
    '168476699247957',
    '170486503106601',
    '169993951339078',
    '170955815162610',
    '169687304410310',
    '169536196560912',
    '168865660143501',
    '168733349958517',
    '171043209071752',
    '169219098945744',
    '171040892999570',
    '165995341998433',
    '169578956923240',
    '170576017060331',
    '170870749601180',
    '169691604717153',
    '170029773227832',
    '164329014103477',
    '169648730795320',
    '163272705111030',
    '170591513074588',
    '169651996846715',
    '167583963457654',
    '167340473771645',
    '166385038887771',
    '171178104462415',
    '167483095643571',
    '168371025962322',
    '166806347824968',
    '167092738394765',
    '163696776668810',
    '167835552234288',
    '167689646744150',
    '167661133249933',
    '170253463062905',
    '170930299764722',
    '166616696169886',
    '166677394402642',
    '167972731107937',
    '170368991668672',
    '166772294722523',
    '166989297411173',
    '170531393183257',
    '170892894353510',
    '168132572231255',
    '164606223667917',
    '167844567066216',
    '170713309312618',
    '168508137507774',
    '167958385994961',
    '169167356910037',
    '168164237281682',
    '169727283549315',
    '166977472887816',
    '169937070330473',
    '170316931718420',
    '170685323741801',
    '169640842927474',
    '164465576844603',
    '167059528849006',
    '169175219064418',
    '170085674170842',
    '169150937049452',
    '170014096950704',
    '170367044342953',
    '168034031651278',
    '166746056174561',
    '168777504864072',
    '170081266559860',
    '168188903959742',
    '170841051072828',
    '170081251051842',
    '170429484957940',
    '162860380605842',
    '168128390071857',
    '165840465668673',
    '170471990809258',
    '168026771423237',
    '170079965345155',
    '167957237937323',
    '170088426957208',
    '169918219218686',
    '164863935551326',
    '168594246306855',
    '170753912554000',
    '169580485703701',
    '170108940431348',
    '170790789782938',
    '171023674023333',
    '166437356833642',
    '170480318899910',
    '170987450917525',
    '170481159704513',
    '171025225994385',
    '164688563741463',
    '170489815798978',
    '170918717235810',
    '170494825211852',
    '167586112317820',
    '169401016789177',
    '169770734187315',
    '168425019664942',
    '167843670433820',
    '166601822090957',
    '170321988832443',
    '164164572399810',
    '170203706180835',
    '169915959533606',
    '167922565279555',
    '170122261923347',
    '161435344380774',
    '163136481434100',
    '165010731430102',
    '169527489506178',
    '170228817119368',
    '168415340244523',
    '164994896107075',
    '168726233110764',
    '169580438137984',
    '170235585643785',
    '168501933299185',
    '167644885426137',
    '169584192843114',
    '164205131246546',
    '167922306240711',
    '167065970630667',
    '169528045173207',
    '170246620490315',
    '168856841367384',
    '170082967018237',
    '169573781536605',
    '170412210261556',
    '161491547752211',
    '171112233781783',
    '170538886336786',
    '169805005322203',
    '169191360575067',
    '166037080099562',
    '170296574048824',
    '170296824062061',
    '170591693423236',
    '170476890692536',
    '169753342032638',
    '169269681767607',
]);
